import { useState } from 'owa-react';
import { FluentButton } from 'owa-fluent-v9-shims';
import SearchRegular from 'owa-fluent-icons-svg/lib/icons/SearchRegular';
import SearchSparkleRegular from 'owa-fluent-icons-svg/lib/icons/SearchSparkleRegular';
import ArrowLeftRegular from 'owa-fluent-icons-svg/lib/icons/ArrowLeftRegular';
import { observer } from 'owa-mobx-react';
import { Icon } from '@fluentui/react/lib/Icon';
import { elementContains } from '@fluentui/react/lib/Utilities';
import { logUsage, PerformanceDatapoint, DatapointStatus } from 'owa-analytics';
import { ErrorBoundary, ErrorComponentType } from 'owa-error-boundary';
import { getApplicationSettings } from 'owa-application-settings';
import loc, { format } from 'owa-localize';
import { SearchPlaceholder as SearchPlaceholderStrings_SearchPlaceholder } from 'owa-search-locstrings/lib/searchPlaceholder.locstring.json';
import {
    CompactSearchBox,
    lazyIsSearchBoxEmpty,
    SearchBoxPillWell,
    SuggestionsCallout,
} from '../lazyFunctions';
import React, { useRef, useEffect, useCallback, useMemo } from 'react';
import { lazyGetHostCapabilitiesProxy } from 'native-outlook-host';
import {
    exitSearch,
    lazyRemoveSuggestionPillFromSearchBox,
    onSearchTextChanged,
    searchBoxBlurred,
    setIsSuggestionsDropdownVisible,
    setSearchTextForSuggestion,
    startSearch,
    startSearchSession,
    suggestionSelected,
    initializeRefinersScenarioStore,
} from 'owa-search-actions';
import {
    SEARCH_SCOPE_SWITCHER_ID,
    SEARCH_SCOPE_SWITCHER_ID_OPTION,
    SEARCH_SCOPE_SWITCHER_ID_LIST,
    SEARCH_SCOPE_SWITCHER_WRAPPER_ID,
    SEARCHBOX_ANCHOR_ID,
    SEARCHBOX_ID,
    SEARCHBOX_COLUMN_CONTAINER_ID,
    LPC_CARD_ID,
} from 'owa-search-constants';
import hasSuggestionPills from '../selectors/hasSuggestionPills';
import { SearchScenarioId, initializeScenarioStore, getScenarioStore } from 'owa-search-store';
import getSearchBoxIdByScenarioId from '../utils/getSearchBoxIdByScenarioId';
import getSearchIconIdByScenarioId from '../utils/getSearchIconIdByScenarioId';
import getSearchBoxMaxWidth from '../utils/getSearchBoxMaxWidth';
import {
    exitSearchTitle as Strings_exitSearchTitle,
    searchButtonTitle as Strings_searchButtonTitle,
    suggestionsAnnouncementPlural as Strings_suggestionsAnnouncementPlural,
    suggestionsAnnouncementSingular as Strings_suggestionsAnnouncementSingular,
} from './SearchBox.locstring.json';
import type { SearchInputHandle } from './SearchInput';
import SearchInput from './SearchInput';
import { Announced } from 'owa-controls-announced';
import { getIsDarkTheme } from 'owa-fabric-theme';
import { useComputedValue } from 'owa-react-hooks/lib/useComputed';

import classNames from 'owa-classnames';
import {
    searchBoxCommonContainer,
    isInCompactMode,
    searchBoxContainerInSearch,
    searchBoxContainer as styles_searchBoxContainer,
    searchBoxContainerNoBorderRadius,
    darkMode,
    draggableRegion,
    nonDraggableRegion,
    lightMode,
    searchBox,
    isDisabled as styles_isDisabled,
    searchIcon,
    searchBoxColumnContainer,
    backButton,
    searchQueryContainer,
    searchButton,
} from './SearchBox.scss';
import { shouldUseAppHostSearch } from 'owa-app-host-search/lib/utils/shouldUseAppHostSearch';
import { getSelectedApp } from 'owa-appbar-state';
import { ExternalAppId } from 'owa-m365-acquisitions/lib/data/M365AppId';
import { getCurrentModule } from 'owa-app-module-store';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';
import { isSparkleIconSupportedForModule } from '../utils/isSparkleIconSupportedForModule';

export interface SearchBoxProps {
    scenarioId: SearchScenarioId; // ID representing the search scenario (unique per scenario)
    customCollapsedMaxWidth?: number; // The max width when the control is collapsed
    renderRefiners?: () => JSX.Element; // Render prop for search refiners button and menu
    searchPlaceHolderText?: string; // Placeholder string to be displayed in search input
    isDisabled?: boolean; // Boolean indicating if search box is disabled (default is false)
    isSuggestCalloutDisabled?: boolean; // Boolean indicating if suggestion callout disabled (default is false)
    isHovered?: boolean;
    onHoverHandlers?: {
        onMouseEnter?: () => void;
        onMouseLeave?: () => void;
    };
    ariaLabel?: string; // Aria label for the search box
    shouldSuppressScopedSuggestions?: boolean; // Boolean indicating whether to turn off scoped suggestions
}

export interface SearchBoxHandle {
    onSearchScopeSelected(scenarioId: SearchScenarioId): Promise<void>;
    focusSearchInput(): void;
}

const leftSideIconNameProps = { iconName: ArrowLeftRegular };
const searchButtonIconProps = { iconName: SearchRegular };

export const SearchBox: React.MemoExoticComponent<
    React.ForwardRefExoticComponent<
        Omit<SearchBoxProps & React.RefAttributes<SearchBoxHandle>, 'ref'> &
            React.RefAttributes<SearchBoxHandle>
    >
> = observer(
    React.forwardRef(function (props: SearchBoxProps, ref: React.Ref<SearchBoxHandle>) {
        const {
            scenarioId,
            customCollapsedMaxWidth,
            renderRefiners,
            searchPlaceHolderText,
            isDisabled,
            isSuggestCalloutDisabled,
            isHovered,
            onHoverHandlers,
            ariaLabel,
            shouldSuppressScopedSuggestions,
        } = props;

        const searchBoxContainerRef = useRef<HTMLDivElement | null>(null);
        const searchInputRef = useRef<SearchInputHandle | null>(null);
        const scenarioStore = getScenarioStore(scenarioId);

        const currentModule = getCurrentModule();
        const mailboxInfo = getModuleContextMailboxInfo();

        const previousSearchActiveValue = useRef(false);
        const [interactiveDraggableRegionsEnabled, setInteractiveDraggableRegionsEnabled] =
            useState(false);

        // Initialize store for scenario on first render
        useEffect(() => {
            initializeScenarioStore(scenarioId);
            initializeRefinersScenarioStore(scenarioId);
        }, []);

        const isInActiveSearchSession = useComputedValue(() => {
            return !!scenarioStore.searchSessionGuid;
        }, [scenarioStore.searchSessionGuid]);

        // Helper function to check if scenario ID matches the current module
        const checkIfCorrectScenarioIdForModule = useCallback(() => {
            let isCorrectScenarioId = false;
            if (currentModule) {
                switch (currentModule) {
                    case 'Mail':
                        isCorrectScenarioId = scenarioId === SearchScenarioId.Mail;
                        break;
                    case 'Calendar':
                        isCorrectScenarioId = scenarioId === SearchScenarioId.Calendar;
                        break;
                    case 'AppHost':
                        isCorrectScenarioId = scenarioId === SearchScenarioId.Mail;
                        break;
                    case 'Groups':
                        isCorrectScenarioId = scenarioId === SearchScenarioId.GroupsMail;
                        break;
                    case 'People':
                    case 'PeopleNew':
                        isCorrectScenarioId = scenarioId === SearchScenarioId.People;
                        break;
                    default:
                        isCorrectScenarioId = false; // Don't double-log in the case of unexpected module
                }
            }
            if (!isCorrectScenarioId) {
                logUsage('SearchBox_ModuleMismatch', mailboxInfo, {
                    scenarioId,
                    currentModule,
                });
            }
        }, [scenarioId, currentModule, mailboxInfo]);

        // Sets focus on SearchInput component
        const focusSearchInput = useCallback(() => {
            /**
             * When trying to set focus on search input while search box is
             * in compact mode, simply start a search session. The focus will get
             * put in search input on component update for expanded search box
             */
            if (scenarioStore.showSearchBoxInCompactMode && !isInActiveSearchSession) {
                startSearchSession('Keyboard', false /* shouldStartSearch */, scenarioId);
            } else {
                searchInputRef.current?.setFocus();
            }
        }, [scenarioStore.showSearchBoxInCompactMode, isInActiveSearchSession]);

        useEffect(() => {
            const initializeInteractiveDraggableRegions = async () => {
                const dp = new PerformanceDatapoint(
                    'Search_InteractiveDraggableRegionsInit',
                    mailboxInfo
                );
                let hasCapabilities = false;
                try {
                    const hostCapabilities = await lazyGetHostCapabilitiesProxy.importAndExecute();
                    if (hostCapabilities !== undefined) {
                        hasCapabilities = true;
                        setInteractiveDraggableRegionsEnabled(
                            hostCapabilities.interactiveDraggableRegionsEnabled
                        );
                    }
                } catch (e) {
                    // Errors resulting from enabling useUnknownInCatchVariables
                    // TS2345: Argument of type 'unknown' is not assignable to parameter of type 'TraceErrorObject | ErrorCodes'.
                    // @ts-expect-error
                    dp.endWithError(DatapointStatus.ClientError, e);
                }

                dp.addCustomData({
                    interactiveDraggableRegionsEnabled,
                    hasHostCapabilities: hasCapabilities,
                });
                dp.end();
            };

            initializeInteractiveDraggableRegions();
            checkIfCorrectScenarioIdForModule();
        }, []);

        /**
         * Focus on search input if the component is transitioning from inactive to
         * active mode while in the compact mode as search input is not present in the DOM
         * when use clicks on the compact search icon so synchronously putting focus on
         * the search input right after the click does not work
         */
        useEffect(() => {
            if (
                scenarioStore.showSearchBoxInCompactMode &&
                isInActiveSearchSession &&
                !previousSearchActiveValue.current
            ) {
                focusSearchInput();
            }

            previousSearchActiveValue.current = isInActiveSearchSession;
        }, [isInActiveSearchSession, focusSearchInput]);

        const isSuggestionsCalloutVisible = useMemo(() => {
            const { isSuggestionsDropdownVisible, currentSuggestions } = scenarioStore;

            return (
                !isSuggestCalloutDisabled &&
                !!currentSuggestions?.Suggestions &&
                isSuggestionsDropdownVisible
            );
        }, [
            scenarioStore.isSuggestionsDropdownVisible,
            scenarioStore.currentSuggestions,
            isSuggestCalloutDisabled,
        ]);

        const searchBoxContainerStyle = useMemo(() => {
            const isSearchBoxExpanded = isInActiveSearchSession;
            const hasHostIcons = getApplicationSettings('PlatformType').isMonarch;
            const showInCompactMode = scenarioStore.showSearchBoxInCompactMode;
            const maxWidth = customCollapsedMaxWidth || 0;

            return {
                maxWidth: `${getSearchBoxMaxWidth(isSearchBoxExpanded, maxWidth)}px`,
                width: hasHostIcons && showInCompactMode && isSearchBoxExpanded ? 'auto' : '100%',
                marginRight:
                    hasHostIcons && showInCompactMode && isSearchBoxExpanded ? '180px' : '0px', // 180px is the width of the host icons
            };
        }, [
            scenarioStore.showSearchBoxInCompactMode,
            customCollapsedMaxWidth,
            isInActiveSearchSession,
        ]);

        const onExit = useCallback(
            (actionSource: string) => {
                if (onHoverHandlers?.onMouseLeave) {
                    onHoverHandlers.onMouseLeave();
                }
                exitSearch(actionSource, scenarioId, false /* forceExit */);
            },
            [scenarioId, onHoverHandlers]
        );

        const onExitClick = useCallback(() => {
            onExit('SearchBoxBackArrow');
        }, []);

        // Start search session when compact search button is clicked
        const onCompactSearchButtonClick = useCallback(() => {
            startSearchSession('SearchCollapsedButton', false /* shouldStartSearch */, scenarioId);
        }, [scenarioId]);

        /**
         * Sets focus on the search input. This is exposed publicly so parent component
         * (SearchBox) can set focus on the input element as a result of clicks on other
         * parts of the search box.
         */
        React.useImperativeHandle(
            ref,
            () => ({
                /**
                 * On clicking search scope picker, sets focus on SearchInput component if the Search box is empty.
                 */
                async onSearchScopeSelected(searchScenarioId: SearchScenarioId) {
                    if (searchInputRef.current) {
                        const isSearchBoxEmptyFunc = await lazyIsSearchBoxEmpty.import();
                        if (isSearchBoxEmptyFunc(searchScenarioId)) {
                            searchInputRef.current.setFocus();
                        }
                    }
                },
                /**
                 * When trying to set focus on search input while search box is
                 * in compact mode, simply start a search session. The focus will get
                 * put in search input on component update for expanded search box
                 */
                focusSearchInput() {
                    if (scenarioStore.showSearchBoxInCompactMode && !isInActiveSearchSession) {
                        startSearchSession('Keyboard', false /* shouldStartSearch */, scenarioId);
                    } else {
                        searchInputRef?.current?.setFocus();
                    }
                },
            }),
            [scenarioStore.showSearchBoxInCompactMode, isInActiveSearchSession]
        );

        // Click handler for the search button
        const onSearchButtonClicked = useCallback(() => {
            return async (evt: React.MouseEvent<unknown>) => {
                evt.stopPropagation();

                const isSearchBoxEmptyFunc = await lazyIsSearchBoxEmpty.import();
                if (!isSearchBoxEmptyFunc(scenarioId)) {
                    // Hide suggestions dropdown
                    setIsSuggestionsDropdownVisible(false, scenarioId);

                    // Special case when in the OrgExplorer app. The first suggestion should automatically be selected
                    if (
                        shouldUseAppHostSearch() &&
                        getSelectedApp() === ExternalAppId.OrgExplorer &&
                        !!scenarioStore.currentSuggestions &&
                        scenarioStore.currentSuggestions.Suggestions?.length > 0
                    ) {
                        suggestionSelected(
                            scenarioStore.currentSuggestions.Suggestions[0],
                            'SearchButton',
                            scenarioId,
                            scenarioStore.currentSuggestions.TraceId ?? '',
                            0
                        );

                        if (!scenarioStore.searchText) {
                            onSearchTextChanged('', scenarioId);
                            setSearchTextForSuggestion('', scenarioId);
                        }
                    } else {
                        startSearch('SearchButton', scenarioId, true /* explicitSearch */);
                    }
                } else {
                    focusSearchInput();
                }
            };
        }, [scenarioStore.currentSuggestions, scenarioStore.searchText, scenarioId]);

        // Click handler to set focus on the SearchInput component
        const onSearchBoxClick = useCallback((evt: React.MouseEvent<unknown>) => {
            evt.stopPropagation();
            focusSearchInput();
        }, []);

        // Focus handler for when an element within the SearchBox component loses focus
        const onSearchBoxBlur = useCallback(
            (evt: React.FocusEvent<EventTarget>) => {
                let relatedTargetElement = evt.relatedTarget as HTMLElement;

                // Fallback for IE, which doesn't always set relatedTarget
                if (!relatedTargetElement) {
                    relatedTargetElement = document.activeElement as HTMLElement;
                }

                const searchScopeButton = document.getElementById(SEARCH_SCOPE_SWITCHER_ID);

                const isTargetElementWithinSearchBox =
                    elementContains(searchBoxContainerRef.current, relatedTargetElement) ||
                    elementContains(searchScopeButton, relatedTargetElement) ||
                    relatedTargetElement.closest?.('[data-advanced-search="true"]') != null ||
                    relatedTargetElement.id == LPC_CARD_ID ||
                    relatedTargetElement.id == SEARCH_SCOPE_SWITCHER_ID_OPTION ||
                    relatedTargetElement.id == SEARCH_SCOPE_SWITCHER_ID_LIST ||
                    relatedTargetElement.id === SEARCH_SCOPE_SWITCHER_ID ||
                    relatedTargetElement.id === SEARCH_SCOPE_SWITCHER_WRAPPER_ID ||
                    (relatedTargetElement.firstElementChild &&
                        relatedTargetElement.firstElementChild.id === SEARCHBOX_ID);

                // Truly "blur" only if event is from outside of search box
                if (!isTargetElementWithinSearchBox) {
                    setIsSuggestionsDropdownVisible(false, scenarioId);
                    searchBoxBlurred(scenarioId);
                }
            },
            [scenarioId]
        );

        // Click handler when user chooses to click the "X" on a pill
        const onRemovePill = useCallback(
            async (suggestionPillId: string) => {
                // Dispatch action to remove suggestion pill from store
                const removeSuggestionPillFromSearchBox =
                    await lazyRemoveSuggestionPillFromSearchBox.import();
                removeSuggestionPillFromSearchBox(suggestionPillId, scenarioId, 'Click');
            },
            [scenarioId]
        );

        const isSearchBoxExpanded = isInActiveSearchSession;

        // Search box should be draggable if the host has enabled draggable regions and it's
        // not in an active search session
        const isDraggable = interactiveDraggableRegionsEnabled && !isSearchBoxExpanded;

        const showCompactMode = scenarioStore.showSearchBoxInCompactMode;

        const searchBoxCommonContainerClassNames = classNames(
            searchBoxCommonContainer,
            showCompactMode && isInCompactMode,
            isSearchBoxExpanded && searchBoxContainerInSearch,
            isDraggable ? draggableRegion : nonDraggableRegion
        );

        const searchBoxContainerClassNames = classNames(
            styles_searchBoxContainer,
            isSearchBoxExpanded && searchBoxContainerNoBorderRadius,
            isSearchBoxExpanded && searchBoxContainerInSearch,
            showCompactMode && isInCompactMode,
            isDraggable ? draggableRegion : nonDraggableRegion
        );

        if (showCompactMode && !isSearchBoxExpanded) {
            return (
                <div
                    role={'search'}
                    id={SEARCHBOX_COLUMN_CONTAINER_ID}
                    className={searchBoxContainerClassNames}
                    ref={searchBoxContainerRef}
                >
                    <CompactSearchBox onClick={onCompactSearchButtonClick} />
                </div>
            );
        }

        const themeColorClass = getIsDarkTheme() ? darkMode : lightMode;
        const searchBoxClassNames = classNames(
            searchBox,
            isDisabled && styles_isDisabled,
            themeColorClass
        );

        const searchGlyphClassNames = classNames(searchIcon, isDisabled && styles_isDisabled);

        // Announce when suggestions are available and visible - include count for screen readers
        const suggestionCount = scenarioStore.currentSuggestions?.Suggestions?.length ?? 0;
        const announcement =
            isSuggestionsCalloutVisible && suggestionCount > 0
                ? suggestionCount === 1
                    ? loc(Strings_suggestionsAnnouncementSingular)
                    : format(loc(Strings_suggestionsAnnouncementPlural), suggestionCount)
                : '';

        const suggestionsComponent = isSuggestionsCalloutVisible ? (
            <SuggestionsCallout
                scenarioId={scenarioId}
                focusSearchInput={focusSearchInput}
                mailboxInfo={mailboxInfo}
                shouldSuppressScopedSuggestions={shouldSuppressScopedSuggestions}
            />
        ) : null;

        const searchBoxColumnClassNames = classNames(
            searchBoxColumnContainer,
            showCompactMode && isInCompactMode
        );

        return (
            /**
             * Outer div is necessary to make this component have a single root element.
             */
            <div style={searchBoxContainerStyle} className={searchBoxCommonContainerClassNames}>
                {/**
                 * Flex column container div is necessary so that the filters anchor div gets proper width
                 */}
                {/* id is used by one-outlook-suite-header to track this div for hit testing */}
                <div className={searchBoxColumnClassNames} id={SEARCHBOX_COLUMN_CONTAINER_ID}>
                    <div
                        role={'search'}
                        tabIndex={-1}
                        className={searchBoxContainerClassNames}
                        ref={searchBoxContainerRef}
                        aria-expanded={isSuggestionsCalloutVisible}
                        onBlur={onSearchBoxBlur}
                        {...onHoverHandlers}
                    >
                        {/* Search box */}
                        <div
                            id={getSearchBoxIdByScenarioId(scenarioId)}
                            className={searchBoxClassNames}
                        >
                            {
                                /* Suggestions UI */
                                <ErrorBoundary type={ErrorComponentType.None}>
                                    {suggestionsComponent}
                                    <Announced message={announcement} />
                                </ErrorBoundary>
                            }

                            {/* Left side (back button or search icon) */}
                            {isSearchBoxExpanded ? (
                                <FluentButton
                                    appearance="icon"
                                    className={classNames(backButton, 'flipForRtl')}
                                    iconProps={leftSideIconNameProps}
                                    onClick={onExitClick}
                                    aria-label={loc(Strings_exitSearchTitle)}
                                    title={loc(Strings_exitSearchTitle)}
                                />
                            ) : (
                                <div
                                    aria-hidden={true}
                                    className={searchGlyphClassNames}
                                    onClick={onSearchBoxClick}
                                    id={getSearchIconIdByScenarioId(scenarioId)}
                                >
                                    <Icon
                                        iconName={
                                            isSparkleIconSupportedForModule(
                                                currentModule,
                                                getSelectedApp()
                                            )
                                                ? SearchSparkleRegular
                                                : SearchRegular
                                        }
                                    />
                                </div>
                            )}

                            {/* Search input (pills and input) */}
                            <div className={searchQueryContainer} onClick={onSearchBoxClick}>
                                {hasSuggestionPills(scenarioId) && (
                                    <SearchBoxPillWell
                                        focusSearchInput={focusSearchInput}
                                        onRemovePill={onRemovePill}
                                        scenarioId={scenarioId}
                                        mailboxInfo={mailboxInfo}
                                    />
                                )}
                                <SearchInput
                                    ref={searchInputRef}
                                    searchPlaceHolderText={searchPlaceHolderText}
                                    isDisabled={isDisabled}
                                    scenarioId={scenarioId}
                                    isSuggestionsCalloutVisible={isSuggestionsCalloutVisible}
                                    ariaLabel={ariaLabel}
                                />
                            </div>

                            {/* Search refiners */}
                            {(isSearchBoxExpanded || isHovered) && renderRefiners && (
                                /**
                                 * This wrapper div with tabindex of -1 is required
                                 * for focus events to have relatedTarget property
                                 * be non-null (because element receiving focus
                                 * needs to be able to receive focus, which divs
                                 * cannot unless given a tabindex value) in Safari.
                                 */
                                <div tabIndex={-1}>{renderRefiners()}</div>
                            )}
                        </div>

                        {/* Search button */}
                        {isSearchBoxExpanded && (
                            <FluentButton
                                appearance="icon"
                                className={searchButton}
                                iconProps={searchButtonIconProps}
                                onClick={onSearchButtonClicked()}
                                aria-label={loc(SearchPlaceholderStrings_SearchPlaceholder)}
                                title={loc(Strings_searchButtonTitle)}
                            />
                        )}
                    </div>
                    {/* Anchor div required for search filters. */}
                    <div id={SEARCHBOX_ANCHOR_ID} />
                </div>
            </div>
        );
    }),
    'SearchBox'
);
